import { Search } from "@mui/icons-material";
import { Box, Button, IconButton, InputAdornment } from "@mui/material";
import { Formik, Form } from "formik";
import { ReactElement, useCallback, useEffect, useState } from "react";
import * as Yup from "yup";

import Table, { TableColumns } from "~frontend/components/Table";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import {
  TagDetail,
  TagGroupDetail,
  TagOrGroupDetail,
} from "~frontend/graphql/tags";
import { palette } from "~frontend/theme";

import {
  nestedTagTableGetChildRows,
  nestedTagTableRenderName,
} from "../../tags/TagTable";
import { usePaginatedTags } from "../../tags/pagination";

export default function GlobalTags({
  tags,
  tagGroups,
  onAddTagsAndGroups,
  integrationTableColumns,
}: {
  tags: TagDetail[];
  tagGroups: TagGroupDetail[];
  onAddTagsAndGroups: (
    tagIds: string[],
    tagGroupIds: string[]
  ) => Promise<void>;
  integrationTableColumns: TableColumns<TagOrGroupDetail>;
}): ReactElement {
  const [search, setSearch] = useState("");

  const filteredTags = usePaginatedTags({
    tags,
    tagGroups,
    search,
  }).data;

  const [selectedTagAndGroupIds, setSelectedTagAndGroupIds] = useState<
    string[]
  >([]);
  const [addingTags, setAddingTags] = useState(false);

  useEffect(() => {
    // Remove any selected tags that are no longer in the filtered list, so
    // they aren't "invisibly" selected.
    const filteredTagIds = new Set(filteredTags.map(tag => tag.id));
    const filteredSelectedTagIds = selectedTagAndGroupIds.filter(id =>
      filteredTagIds.has(id)
    );
    if (filteredSelectedTagIds.length !== selectedTagAndGroupIds.length) {
      setSelectedTagAndGroupIds(filteredSelectedTagIds);
    }
  }, [filteredTags, selectedTagAndGroupIds, setSelectedTagAndGroupIds]);

  const handleAddTagsAndGroups = useCallback(
    async (tagAndGroupIds: string[]) => {
      setAddingTags(true);

      const tagIds =
        tags
          .filter(tag => tagAndGroupIds.includes(tag.id))
          .map(tag => tag.id) ?? [];

      const tagGroupIds =
        tagGroups
          .filter(tagGroup => tagAndGroupIds.includes(tagGroup.id))
          .map(tagGroup => tagGroup.id) ?? [];

      try {
        await onAddTagsAndGroups(tagIds, tagGroupIds);
        setSelectedTagAndGroupIds([]);
      } finally {
        setAddingTags(false);
      }
    },
    [
      setAddingTags,
      onAddTagsAndGroups,
      setSelectedTagAndGroupIds,
      tagGroups,
      tags,
    ]
  );

  return (
    <>
      <Formik
        initialValues={{ search }}
        validationSchema={Yup.object().shape({
          search: Yup.string(),
        })}
        onSubmit={values => setSearch(values.search)}
      >
        {({ submitForm }) => (
          <Form style={{ flex: 1 }}>
            {/* TODO: enter to submit */}
            <FormikTextField
              fullWidth
              name="search"
              onBlur={submitForm}
              placeholder="Search for a tag"
              sx={{ pt: 0, mb: 1 }}
              InputProps={{
                sx: { fontSize: "12px", color: palette.darkGray },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={submitForm}>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Form>
        )}
      </Formik>
      <Box sx={{ height: "300px", overflow: "scroll", pb: 3 }}>
        <Table
          size="small"
          columns={[
            {
              key: "name",
              title: "Tag Name",
              renderValue: nestedTagTableRenderName,
            },
            ...integrationTableColumns,
          ]}
          rows={filteredTags}
          noFooter
          selectedRows={selectedTagAndGroupIds}
          onChangeSelectedRows={setSelectedTagAndGroupIds}
          selectDisabled={addingTags}
          getChildRows={nestedTagTableGetChildRows}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
        columnGap={1}
      >
        <Button
          variant="contained"
          color="tertiary"
          sx={{
            backgroundColor: palette.midGray,
            "&:hover": {
              backgroundColor: palette.darkGray,
              color: palette.white,
            },
          }}
          disabled={addingTags || tags.length === 0}
          onClick={() =>
            handleAddTagsAndGroups(
              tags.map(tag => tag.id).concat(tagGroups.map(group => group.id))
            )
          }
        >
          Import All Tags
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          sx={
            addingTags || selectedTagAndGroupIds.length === 0
              ? {
                  backgroundColor: `${palette.lightGray} !important`,
                  border: `1px solid ${palette.midGray} !important`,
                  color: palette.navy,
                }
              : {}
          }
          disabled={addingTags || selectedTagAndGroupIds.length === 0}
          onClick={() => handleAddTagsAndGroups(selectedTagAndGroupIds)}
        >
          Import Selected
        </Button>
      </Box>
    </>
  );
}
